import { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next/typescript/t";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormBaseDropdown,
  HookFormInfiniteScrollDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  useLazyGetAchievementsQuery,
  useLazyGetCommunityRolesQuery,
  useLazyGetCountriesQuery,
} from "@/redux/service";
import {
  CountryResponse,
  CountryQueryParams,
  AchievementResponse,
  AchievementQueryParams,
  CommunityRoleResponse,
  CommunityRoleQueryParams,
} from "@/redux/common/types";
import {
  Chapters,
  OrganizationCommunicationStatuses,
  RegistrationStatus,
} from "@/shared/utils/constants";
import {
  OrganizationFiltersProps,
  OrganizationFiltersState,
} from "@/features/organizations/list/types";
import { useFilterCount } from "@/shared/hooks/useFilterCount";

import "../styles.css";

const organizationFiltersSchema = (
  t: TFunction,
): yup.ObjectSchema<Omit<OrganizationFiltersState, "isInternal" | "exclude">> =>
  yup
    .object()
    .shape({
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .typeError(t("form.validation.notValid")),
      countries: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      chapters: yup
        .array()
        .of(yup.string().required().typeError(t("form.validation.notValid"))),
      externalId: yup.string().typeError(t("form.validation.notValid")),
      registrationStatus: yup.string().typeError(t("form.validation.notValid")),
      communicatingStatus: yup
        .array()
        .of(yup.string().required().typeError(t("form.validation.notValid"))),
      achievements: yup
        .array(
          yup
            .number()
            .required(t("form.validation.notValid"))
            .typeError(t("form.validation.notValid")),
        )
        .nullable(),
      communityRoles: yup
        .array(
          yup
            .number()
            .required(t("form.validation.notValid"))
            .typeError(t("form.validation.notValid")),
        )
        .nullable(),
      excludeInvitedToSurvey: yup
        .number()
        .typeError(t("form.validation.notValid")),
    })
    .required();

const OrganizationFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
}: OrganizationFiltersProps) => {
  const { t } = useTranslation();

  const [getCountries] = useLazyGetCountriesQuery();
  const [getAchievements] = useLazyGetAchievementsQuery();
  const [getCommunityRoles] = useLazyGetCommunityRolesQuery();

  const form = useForm<OrganizationFiltersState>({
    resolver: yupResolver(organizationFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: undefined,
      countries: [],
      chapters: [],
      externalId: undefined,
      registrationStatus: undefined,
      communicatingStatus: undefined,
      achievements: [],
      communityRoles: [],
      ...defaultFilters,
    },
  });

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            className="organization-filters-search"
            suffix={<SearchOutlined />}
            placeholder={t("organizations.placeholders.searchOrganizations")}
          />
        ),
      },
      achievements: {
        name: "achievements",
        label: t("organizations.labels.achievements"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<
            AchievementResponse,
            AchievementQueryParams
          >
            name="achievements"
            control={form.control}
            fetchFn={getAchievements}
            labelFieldName="label"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="label"
            multiple
            placeholder={t("organizations.placeholders.selectAchievements")}
            allowClear={true}
            defaultFilters={{
              isAssigned: true,
              type: "organization",
            }}
          />
        ),
      },
      communityRoles: {
        name: "communityRoles",
        label: t("organizations.labels.communityRoles"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<
            CommunityRoleResponse,
            CommunityRoleQueryParams
          >
            name="communityRoles"
            control={form.control}
            fetchFn={getCommunityRoles}
            labelFieldName="label"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="label"
            multiple
            placeholder={t("organizations.placeholders.selectCommunityRoles")}
            allowClear={true}
            defaultFilters={{
              isAssigned: true,
              type: "organization",
            }}
          />
        ),
      },
      countries: {
        name: "countries",
        label: t("organizations.labels.countries"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<CountryResponse, CountryQueryParams>
            name="countries"
            control={form.control}
            fetchFn={getCountries}
            labelFieldName="name"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="name"
            multiple
            placeholder={t("organizations.placeholders.selectCountries")}
            allowClear={true}
          />
        ),
      },
      chapters: {
        name: "chapters",
        label: t("organizations.labels.chapters"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="chapters"
            multiple
            control={form.control}
            options={Chapters.asList()}
            placeholder={t("organizations.placeholders.selectChapters")}
            allowClear={true}
          />
        ),
      },
      externalId: {
        name: "externalId",
        label: t("organizations.labels.externalId"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInput
            name="externalId"
            control={form.control}
            placeholder={t("organizations.placeholders.enterExternalId")}
          />
        ),
      },
      registrationStatus: {
        name: "registrationStatus",
        label: t("organizations.labels.registrationStatus"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="registrationStatus"
            control={form.control}
            options={RegistrationStatus.asList()}
            placeholder={t(
              "organizations.placeholders.selectRegistrationStatus",
            )}
            allowClear={true}
          />
        ),
      },
      communicatingStatus: {
        name: "communicatingStatus",
        label: t("organizations.labels.communicatingStatus"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="communicatingStatus"
            control={form.control}
            multiple
            options={OrganizationCommunicationStatuses.asList()}
            placeholder={t(
              "organizations.placeholders.selectCommunicatingStatuses",
            )}
            allowClear={true}
          />
        ),
      },
    };
  }, []);

  const activeFilterCount = useFilterCount<OrganizationFiltersState>(
    form,
    defaultFilters,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("organizations.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFilterCount}
      handleSearch={() => setFilters(form.getValues())}
      className="organizations-filters"
    />
  );
};

export default OrganizationFilters;
